<template>
  <div class="relative version-field">
    <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Version<span class="text-red-500">*</span></label>
    <div class="relative">
      <div ref="triggerEl"
        :class="open ? 'rounded-t-lg border-b-transparent bg-gray-50 dark:bg-white/[0.04]' : 'rounded-lg bg-white dark:bg-[#0d1117]'"
        class="w-full h-[42px] flex items-center gap-2 pl-3.5 pr-3 border border-gray-200 dark:border-gray-700">
        <input ref="inputEl" v-model="search" type="text" placeholder="Search versions…" autocomplete="off"
          @focus="openPanel" @input="openPanel" @keydown="handleKeydown"
          class="flex-1 min-w-0 text-sm bg-transparent border-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 placeholder:text-xs focus:outline-none focus:ring-0" />
        <span v-if="selectedVersion && !open && versionEolStatus(selectedVersion) !== 'none'"
          class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold" :class="VERSION_EOL_BADGE_CLASSES[versionEolStatus(selectedVersion)]">
          {{ VERSION_EOL_BADGE_LABELS[versionEolStatus(selectedVersion)] }}
        </span>
        <svg class="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform" :class="open ? 'rotate-180' : ''"
          fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
        </svg>
      </div>
    </div>

    <!-- Teleported to <body> and positioned with `fixed`, flush against the
         trigger's bottom edge (no gap, no top border) — same fused combobox
         look as AddApplicationModal's Name field / CriticalityField, just
         escaping the factsheet card's overflow clipping via Teleport. -->
    <Teleport to="body">
      <div v-if="open" class="fixed inset-0 z-[9998]" @click="closePanel(true)"></div>
      <div v-if="open" :style="panelStyle"
        class="fixed z-[9999] bg-white dark:bg-[#1C2128] border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-lg shadow-lg py-1.5 max-h-72 overflow-y-auto">
        <button v-for="(option, index) in filteredVersions" :key="option.id" type="button"
          :ref="(el) => setOptionRef(el, index)"
          @click="select(option)" @mouseenter="activeIndex = index"
          :class="activeIndex === index ? 'bg-gray-50 dark:bg-gray-800' : (modelValue === option.id ? 'bg-blue-50 dark:bg-blue-900/20' : '')"
          class="w-full flex items-start justify-between gap-2 text-left px-3.5 py-2 transition-colors cursor-pointer">
          <span class="min-w-0">
            <p class="text-sm font-medium truncate" :class="modelValue === option.id ? 'text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'">
              {{ option.version }}
            </p>
            <p v-if="option.alias" class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{{ option.alias }}</p>
          </span>
          <span v-if="versionEolStatus(option) !== 'none'" class="flex-shrink-0 mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold" :class="VERSION_EOL_BADGE_CLASSES[versionEolStatus(option)]">
            {{ VERSION_EOL_BADGE_LABELS[versionEolStatus(option)] }}
          </span>
        </button>

        <p v-if="search.trim() && filteredVersions.length === 0" class="px-3.5 py-3 text-xs text-gray-400 dark:text-gray-500 text-center">No matching version.</p>

        <!-- Always visible regardless of search, and the last stop in
             keyboard navigation (wraps back to the first option past this point). -->
        <div class="border-t border-gray-100 dark:border-gray-700 mt-1 pt-1.5">
          <button type="button" :ref="(el) => setOptionRef(el, lastIndex)"
            @click="addNew" @mouseenter="activeIndex = lastIndex"
            :class="activeIndex === lastIndex ? 'bg-blue-50 dark:bg-blue-900/20' : ''"
            class="w-full flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 rounded-md transition-colors cursor-pointer">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
            Add a new version
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useOrgComponentVersionsStore } from 'stores/orgComponentVersions.js'
import { versionEolStatus, VERSION_EOL_BADGE_CLASSES, VERSION_EOL_BADGE_LABELS } from 'controllers/vue_components/organizations/applications/versionEolStatus.js'

const props = defineProps({
  // An application always has a version selected — never "not specified" —
  // so this is required, with no empty-string default.
  modelValue: { type: String, required: true },
  orgComponentId: { type: String, required: true },
})

// add-new: the caller is responsible for opening the versions manager and
// its add-version form — this component only knows about picking a value.
const emit = defineEmits(['update:modelValue', 'add-new'])

const versionsStore = useOrgComponentVersionsStore()
const versions = computed(() => versionsStore.entryFor(props.orgComponentId).records)

const open = ref(false)
const search = ref('')
const activeIndex = ref(-1)
const optionRefs = ref([])
const triggerEl = ref(null)
const panelStyle = ref({})

const selectedVersion = computed(() => versions.value.find((v) => v.id === props.modelValue))

function versionLabel(option) {
  return option ? option.version : ''
}

// The input doubles as both the search box and the selected-value display —
// keep its text in sync with the selected version whenever it changes
// elsewhere (e.g. right after bootstrapping a default version on load).
watch(selectedVersion, (option) => {
  search.value = versionLabel(option)
}, { immediate: true })

const filteredVersions = computed(() => {
  const query = search.value.trim().toLowerCase()
  if (!query) return versions.value
  return versions.value.filter((v) =>
    v.version.toLowerCase().includes(query) || (v.alias || '').toLowerCase().includes(query))
})

const lastIndex = computed(() => filteredVersions.value.length)

function setOptionRef(el, index) {
  if (el) optionRefs.value[index] = el
}

function positionPanel() {
  const rect = triggerEl.value.getBoundingClientRect()
  return {
    top: `${rect.bottom}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  }
}

async function openPanel() {
  if (open.value) return
  // Clear the box on open so the full version list shows rather than
  // immediately self-filtering down to whatever the display text (the
  // current selection) happens to match — the input only doubles as a
  // filter once the user actually types something new.
  search.value = ''
  panelStyle.value = positionPanel()
  open.value = true

  // Clearing search above changes filteredVersions, which the watcher below
  // resets activeIndex for — wait for that flush before setting the "current
  // selection is highlighted" index, or it would immediately get stomped
  // back to -1.
  await nextTick()
  const selectedIndex = filteredVersions.value.findIndex((v) => v.id === props.modelValue)
  activeIndex.value = selectedIndex >= 0 ? selectedIndex : -1
}

// snapBack reverts unsaved search text back to the selected version's label
// when the panel closes without a pick — mirrors AddItComponentSlideOver's
// Type field.
function closePanel(snapBack = false) {
  open.value = false
  activeIndex.value = -1
  if (snapBack) search.value = versionLabel(selectedVersion.value)
}

function select(option) {
  emit('update:modelValue', option.id)
  search.value = versionLabel(option)
  closePanel()
}

function addNew() {
  closePanel(true)
  emit('add-new')
}

watch(activeIndex, (index) => {
  if (index >= 0) optionRefs.value[index]?.scrollIntoView({ block: 'nearest' })
})

watch(filteredVersions, () => {
  activeIndex.value = -1
  optionRefs.value = []
})

function handleKeydown(e) {
  if (!open.value) {
    if (e.key === 'ArrowDown' || e.key === 'Enter') {
      e.preventDefault()
      openPanel()
    }
    return
  }

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    activeIndex.value = activeIndex.value >= lastIndex.value ? 0 : activeIndex.value + 1
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    activeIndex.value = activeIndex.value <= 0 ? lastIndex.value : activeIndex.value - 1
  } else if (e.key === 'Enter') {
    e.preventDefault()
    if (activeIndex.value === lastIndex.value) addNew()
    else if (activeIndex.value >= 0) select(filteredVersions.value[activeIndex.value])
  } else if (e.key === 'Escape') {
    closePanel(true)
  }
}
</script>
